import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { PortalRegistrationState } from "@/Common/Models/Portal/Enums/PortalRegistrationState";
import { SharedItemPriority } from "@/Common/Models/Portal/SharedItem/SharedItemPriority";
import { IPortalLinkedItemModel } from "@/Common/Models/Portal/UI/IPortalLinkedItemModel";
import { BillingPaymentMethod } from "@/Common/Models/UI/BillingPaymentMethod";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { PatientPrivateBillItem } from "@/Patient/Models/History/PatientPrivateBillItem";
import { sharedItemProviderFactory } from "@/Portal/Services/SharedItemProviderFactory";
import { BaseSendToPortalPopupStrategy } from "@/Portal/Services/Strategies/BaseSendToPortalPopupStrategy";

class PrivateBillEditSendToPortalPopupStrategy extends BaseSendToPortalPopupStrategy {
    public readonly itemCheckOperation: number;
    public readonly sharedItemTypeLabel: string;
    public readonly canEditMessage: boolean;
    public readonly linkedItemType: PortalLinkedItemType;
    public readonly disclaimer: string;
    public readonly sendAndPrintLabel: string;
    public readonly sendToPortalLabel: string;

    public onClose: () => void;
    public patientId: string;

    constructor(
        onClose: () => void,
        patientId: string,
        isBillClosed: boolean,
        paymentMethod: BillingPaymentMethod
    ) {
        super(PortalLinkedItemType.PrivateBill, null, null);
        this.sendToPortalLabel = cacheStore.GetString("SyncLabel");
        this.sendAndPrintLabel = cacheStore.GetString("SyncAndPrintLabel");
        this.disclaimer = isBillClosed ? "" : this.getDisclaimerMessage(paymentMethod);

        this.linkedItemType = PortalLinkedItemType.PrivateBill;
        this.canEditMessage = true;
        this.sharedItemTypeLabel = sharedItemProviderFactory.get(PortalLinkedItemType.PrivateBill).getSharedItemTypeLabel();

        this.onClose = onClose;
        this.patientId = patientId;
    }

    public get canCheckItem(): boolean {
        return true;
    }

    public get itemIconClass(): string {
        return "billingInvoiceIcon";
    }

    public async onSendToPortalClick(subject: string, newMessage: string, includeDocument: boolean, _portalItemModel?: IPortalLinkedItemModel, priority?: SharedItemPriority, canPatientReplyWithMessage?: boolean): Promise<void> {
        sessionStore.dispatch(
            LocalPatientOperations.History.PrivateBill_Billing_SaveAndSyncNewBillToPortal,
            {
                newMessage,
                includeDocument,
                subject,
                shouldGeneratePDF: false,
                priority: priority,
                canPatientReplyWithMessage: canPatientReplyWithMessage
            }
        );

        this.onClose();
    }

    public onSendAndPrintClick(subject: string, newMessage: string, includeDocument: boolean, _portalItemModel?: IPortalLinkedItemModel, priority?: SharedItemPriority, canPatientReplyWithMessage?: boolean): void {
        sessionStore.dispatch(
            LocalPatientOperations.History.PrivateBill_Billing_SaveAndSyncNewBillToPortal,
            {
                newMessage,
                includeDocument,
                subject,
                shouldGeneratePDF: true,
                priority: priority,
                canPatientReplyWithMessage
            }
        );

        this.onClose();
    }

    public onRevokeClick(): void {
        // revoke action doesnt show for created bill
    }

    public onClosePopupClick(): void {
        this.onClose();
    }

    public static getDefaultValueForSyncToPortalModel(
        portalSyncState: PortalRegistrationState,
        individualResponseContactId: string,
        dateOfVisit: Date
    ): IPortalLinkedItemModel {
        return Immutable.fromJS({
            RegistrationStatus: portalSyncState,
            PatientContactId: individualResponseContactId,
            LinkedItemMessages: Immutable.List([]),
            LinkedItemType: PortalLinkedItemType.PrivateBill,
            IsSending: false,
            DefaultSubject: `${dateOfVisit?.format("YYYY-MM-DD")} #`,
            LinkedItemModel: new PatientPrivateBillItem({
                BillNumber: 0,
                BillDate: dateOfVisit?.format("YYYY/MM/DD")
            })
        });
    }

    private getDisclaimerMessage(paymentMethod: BillingPaymentMethod): string {
        return paymentMethod === BillingPaymentMethod.Payable ?
            cacheStore.GetString("DisclaimerPortalPayableBillWarningLabel") :
            cacheStore.GetString("DisclaimerPortalWarningLabel");
    }
}

export { PrivateBillEditSendToPortalPopupStrategy };
